import { useEffect, useRef, useState } from 'react';
import { Button } from './components';

export interface LatLng {
  lat: number;
  lng: number;
}

/** Centro de México: vista inicial cuando la iglesia aún no pone su marcador. */
const START: [number, number] = [23.6, -102.5];
const round = (n: number) => Math.round(n * 1e6) / 1e6;

/**
 * Mapa interactivo (Leaflet + OpenStreetMap) para colocar un marcador:
 * toca el mapa para ponerlo, arrástralo para ajustarlo o usa la ubicación del dispositivo.
 */
export function LocationPicker({ value, onChange, label = 'Ubicación', readOnly = false }: { value: LatLng | null; onChange?: (v: LatLng | null) => void; label?: string; readOnly?: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<import('leaflet').Map | null>(null);
  const marker = useRef<import('leaflet').Marker | null>(null);
  const lib = useRef<typeof import('leaflet') | null>(null);
  const change = useRef(onChange ?? (() => undefined));
  change.current = onChange ?? (() => undefined);
  const [error, setError] = useState(false);
  const [gps, setGps] = useState<'idle' | 'busy' | 'denied'>('idle');

  function place(v: LatLng, zoom?: number) {
    const L = lib.current;
    const m = map.current;
    if (!L || !m) return;
    if (marker.current) marker.current.setLatLng([v.lat, v.lng]);
    else {
      const icon = L.divIcon({ className: 'map-pin', html: '<span></span>', iconSize: [28, 28], iconAnchor: [14, 28] });
      marker.current = L.marker([v.lat, v.lng], { draggable: !readOnly, icon, keyboard: true, title: 'Ubicación de la iglesia' }).addTo(m);
      marker.current.on('dragend', () => {
        const p = marker.current!.getLatLng();
        change.current({ lat: round(p.lat), lng: round(p.lng) });
      });
    }
    if (zoom) m.setView([v.lat, v.lng], zoom);
  }

  useEffect(() => {
    let disposed = false;
    void (async () => {
      try {
        const L = (await import('leaflet')).default;
        await import('leaflet/dist/leaflet.css');
        if (disposed || !box.current) return;
        lib.current = L;
        const m = L.map(box.current, { scrollWheelZoom: false }).setView(value ? [value.lat, value.lng] : START, value ? 16 : 5);
        map.current = m;
        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>',
        }).addTo(m);
        if (value) place(value);
        if (!readOnly) m.on('click', (e) => {
          const v = { lat: round(e.latlng.lat), lng: round(e.latlng.lng) };
          place(v);
          change.current(v);
        });
      } catch {
        if (!disposed) setError(true);
      }
    })();
    return () => {
      disposed = true;
      map.current?.remove();
      map.current = null;
      marker.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function useDevice() {
    if (!('geolocation' in navigator)) return setGps('denied');
    setGps('busy');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const v = { lat: round(pos.coords.latitude), lng: round(pos.coords.longitude) };
        place(v, 17);
        change.current(v);
        setGps('idle');
      },
      () => setGps('denied'),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  }

  function clear() {
    marker.current?.remove();
    marker.current = null;
    change.current(null);
  }

  if (readOnly) {
    return value ? (
      <div ref={box} className="atlas__map location-picker__map" role="region" aria-label={label} />
    ) : (
      <p className="small muted">Sin marcador en el mapa.</p>
    );
  }

  return (
    <div className="location-picker">
      <p className="small muted">
        {label}: toca el mapa donde está la iglesia; puedes arrastrar el marcador para ajustarlo. Así aparece en «Busca una iglesia» cerca de quien la busca.
      </p>
      {error ? <p className="muted">No se pudo cargar el mapa.</p> : <div ref={box} className="atlas__map location-picker__map" role="region" aria-label="Mapa para colocar la ubicación" />}
      <div className="actions">
        <Button type="button" variant="secondary" busy={gps === 'busy'} onClick={useDevice}>
          Usar mi ubicación actual
        </Button>
        {value ? (
          <Button type="button" variant="ghost" onClick={clear}>
            Quitar marcador
          </Button>
        ) : null}
      </div>
      <p className="small muted" aria-live="polite">
        {gps === 'denied'
          ? 'No pudimos usar tu ubicación. Revisa el permiso del navegador o toca el mapa.'
          : value
            ? `Marcador en ${value.lat.toFixed(5)}, ${value.lng.toFixed(5)}.`
            : 'Todavía no hay marcador.'}
      </p>
    </div>
  );
}
